import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GridColDef } from '@mui/x-data-grid-pro'
import { Tooltip } from '@mui/material'
import IconButton from '@/shared/components/IconButton/IconButton'
import Chip from '@/shared/components/Chip/Chip'
import ChipsCell from '@/shared/components/Table/components/ChipsCell/ChipsCell'
import styles from '../RewardColumns.module.scss'

export const useRewardColumnsConfig = (
  handleOnEdit: (id: string) => void,
  handleOnDelete: (id: string) => void,
  handleOnToggleActive: (id: string) => void
) => {
  const { t } = useTranslation()

  return useMemo<GridColDef[]>(
    () => [
      {
        field: 'dishes',
        headerName: t('TABLE.LOYALTY.HEADERS.REWARD'),
        editable: false,
        hideable: false,
        disableColumnMenu: true,
        // Ids resolve to several names, so sorting on the raw value would sort
        // on an array. The joined labels are what the manager reads, so that is
        // what it sorts on too.
        sortComparator: (a, b) =>
          (a ?? [])
            .map((chip: { label: string }) => chip.label)
            .join(', ')
            .localeCompare(
              (b ?? []).map((chip: { label: string }) => chip.label).join(', ')
            ),
        headerClassName: styles.tableHeader,
        cellClassName: styles.tableCell,
        flex: 2,
        minWidth: 160,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} body-bold-medium`}>
            {params.colDef.headerName}
          </p>
        ),
        // The same cell the dashboard uses for its "ordered with" column: chips
        // that collapse to a "+N" with the rest behind a tooltip.
        renderCell: params => <ChipsCell chipPayload={params.value} />
      },
      {
        field: 'points',
        headerName: t('TABLE.LOYALTY.HEADERS.POINTS'),
        editable: false,
        disableColumnMenu: true,
        headerClassName: styles.tableHeader,
        cellClassName: styles.tableCell,
        flex: 1,
        minWidth: 120,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} body-bold-medium`}>
            {params.colDef.headerName}
          </p>
        ),
        renderCell: params => (
          <span className={params.row.isActive ? '' : styles.inactiveCell}>
            {t('LOYALTY.REWARDS.POINTS_VALUE', { count: params.value })}
          </span>
        )
      },
      {
        field: 'isActive',
        headerName: t('TABLE.LOYALTY.HEADERS.STATUS'),
        editable: false,
        disableColumnMenu: true,
        headerClassName: styles.tableHeader,
        cellClassName: styles.tableCell,
        flex: 1,
        minWidth: 120,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} body-bold-medium`}>
            {params.colDef.headerName}
          </p>
        ),
        /*
          Hidden, not inactive: the guest sees exactly the same thing whether a
          reward was hidden or deleted, so the two have to be told apart here.
          Hidden means still on this list and coming back; deleted means gone
          from it. The word is the first half of that; the confirmation on the
          bin is the second.
        */
        renderCell: params => (
          <Chip
            label={
              params.value
                ? t('LOYALTY.REWARDS.ACTIVE')
                : t('LOYALTY.REWARDS.HIDDEN')
            }
            color={params.value ? 'secondary' : 'primary'}
          />
        )
      },
      {
        field: 'actions',
        headerName: t('TABLE.LOYALTY.HEADERS.ACTIONS'),
        sortable: false,
        editable: false,
        hideable: false,
        disableColumnMenu: true,
        // Pinned the way every other actions column in this app is: three icon
        // buttons need exactly this much room, so dragging it wider or narrower
        // only ever makes it wrong. Width, min and max agree, and reordering is
        // off so it cannot be dragged out from the end of the row either.
        resizable: false,
        disableReorder: true,
        headerClassName: styles.tableHeader,
        cellClassName: styles.tableCell,
        width: 108,
        minWidth: 108,
        maxWidth: 108,
        renderHeader: params => (
          <p className={`${styles.tableHeaderText} body-bold-medium`}>
            {params.colDef.headerName}
          </p>
        ),
        /*
          The eye and the bin do the same thing to a guest, so each says which
          one it is on hover: hiding keeps the reward on the list, deleting does
          not. Tooltips rather than labels — three labelled buttons would not
          fit a row, and the icons are the repo's own (`la-eye`/`la-eye-slash`
          are already the show/hide pair in TextField and ImageCropper).
        */
        renderCell: params => (
          <div className={styles.rewardActions}>
            <Tooltip
              title={
                params.row.isActive
                  ? t('LOYALTY.REWARDS.HIDE_TOOLTIP')
                  : t('LOYALTY.REWARDS.SHOW_TOOLTIP')
              }
            >
              {/* Whether a reward is offered is one click, not a form. */}
              <span>
                <IconButton
                  color="primary"
                  icon={
                    params.row.isActive
                      ? 'las la-eye icon-medium'
                      : 'las la-eye-slash icon-medium'
                  }
                  onClick={() => handleOnToggleActive(params.row.id)}
                />
              </span>
            </Tooltip>
            <IconButton
              color="primary"
              icon="las la-edit icon-medium"
              onClick={() => handleOnEdit(params.row.id)}
            />
            <Tooltip title={t('LOYALTY.REWARDS.DELETE_TOOLTIP')}>
              <span>
                <IconButton
                  color="primary"
                  icon="las la-trash icon-medium"
                  onClick={() => handleOnDelete(params.row.id)}
                />
              </span>
            </Tooltip>
          </div>
        )
      }
    ],
    [t, handleOnEdit, handleOnDelete, handleOnToggleActive]
  )
}
